import React, { useState, useEffect, useCallback } from 'react';
import { vehiclesApi, formatApiError } from '../services/api';
import {
  Car,
  TrendUp,
  TrendDown,
  ChartBar,
  CurrencyDollar,
  Wrench,
  User,
  CalendarBlank,
  ArrowsDownUp,
  Eye,
  X,
  Percent,
  ArrowCounterClockwise,
  Bank,
  DownloadSimple
} from '@phosphor-icons/react';

const MONTH_NAMES = {
  '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
  '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
  '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
};

const SortHeader = ({ field, sortField, onSort, children }) => (
  <th className="cursor-pointer select-none" onClick={() => onSort(field)}>
    <span className="flex items-center gap-1">
      {children}
      <ArrowsDownUp size={12} className={sortField === field ? 'text-[#0055FF]' : 'text-[#CBD5E1]'} />
    </span>
  </th>
);

const Profitability = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('net_income');
  const [sortDir, setSortDir] = useState(-1);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [availableMonths, setAvailableMonths] = useState([]);

  const loadData = useCallback(async (period) => {
    try {
      setLoading(true);
      const params = {};
      if (period && period !== 'all') {
        const [y, m] = period.split('-');
        params.year = parseInt(y);
        params.month = parseInt(m);
      }
      const { data: result } = await vehiclesApi.getAllProfitability(params);
      setData(result);
      if (result.available_months) {
        setAvailableMonths(result.available_months);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(selectedPeriod); }, [selectedPeriod, loadData]);

  const fmt = (n) => `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d * -1);
    else { setSortField(field); setSortDir(-1); }
  };

  const formatMonthLabel = (ym) => {
    if (!ym || ym === 'all') return 'Historico (Todo)';
    const [y, m] = ym.split('-');
    return `${MONTH_NAMES[m] || m} ${y}`;
  };

  const handleDownloadExcel = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    let url = `${base}/api/reports/excel/profitability`;
    if (selectedPeriod && selectedPeriod !== 'all') {
      const [y, m] = selectedPeriod.split('-');
      url += `?year=${parseInt(y)}&month=${parseInt(m)}`;
    }
    window.open(url, '_blank');
  };

  const sorted = data ? [...data.vehicles].sort((a, b) => {
    const va = a[sortField] || 0, vb = b[sortField] || 0;
    return sortDir === 1 ? va - vb : vb - va;
  }) : [];

  if (loading && !data) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  if (error) return <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>;
  if (!data) return null;

  const { totals } = data;
  const topPerformer = sorted.find(v => v.total_income > 0);
  const bottomPerformer = [...sorted].reverse().find(v => v.total_income > 0);
  const isFiltered = selectedPeriod !== 'all';

  const financialByTypeSorted = Object.entries(totals.financial_by_type || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div data-testid="profitability-page">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0F172A]">Rentabilidad por Vehiculo</h1>
          <p className="text-sm text-[#64748B]">
            {totals.vehicles_count} vehiculos | {totals.active_contracts} contratos activos
            {isFiltered && <span className="ml-2 text-[#0055FF] font-medium">| {formatMonthLabel(selectedPeriod)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CalendarBlank size={18} className="text-[#64748B]" />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="input-field text-sm min-w-[200px]"
              data-testid="period-selector"
            >
              <option value="all">Historico (Todo)</option>
              {availableMonths.map(m => (
                <option key={m} value={m}>{formatMonthLabel(m)}</option>
              ))}
            </select>
          </div>
          {isFiltered && (
            <button
              onClick={() => setSelectedPeriod('all')}
              className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded-lg transition-colors"
              title="Ver historico completo"
              data-testid="reset-period-btn"
            >
              <ArrowCounterClockwise size={18} />
            </button>
          )}
          <button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
            title="Descargar Excel con el filtro actual"
            data-testid="export-profitability-excel"
          >
            <DownloadSimple size={16} weight="bold" />
            Excel
          </button>
        </div>
      </div>

      {loading && <div className="mb-4 text-center text-sm text-[#64748B]">Cargando datos...</div>}

      {/* Period Indicator */}
      {isFiltered && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-6 flex items-center justify-between">
          <p className="text-sm text-blue-700 font-medium">
            Mostrando datos de: <span className="font-bold">{formatMonthLabel(selectedPeriod)}</span>
          </p>
          <button
            onClick={() => setSelectedPeriod('all')}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Ver Historico Completo
          </button>
        </div>
      )}

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <CurrencyDollar size={16} className="text-emerald-500" />
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Ingresos</p>
          </div>
          <p className="text-xl font-bold text-emerald-600" data-testid="total-income">{fmt(totals.total_income)}</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">{isFiltered ? formatMonthLabel(selectedPeriod) : 'Acumulado total'}</p>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <Wrench size={16} className="text-amber-500" />
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Mantenimiento</p>
          </div>
          <p className="text-xl font-bold text-amber-600" data-testid="total-maintenance">{fmt(totals.total_maintenance)}</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">{isFiltered ? formatMonthLabel(selectedPeriod) : 'Acumulado total'}</p>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <Bank size={16} className="text-rose-500" />
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Gasto Financiero</p>
          </div>
          <p className="text-xl font-bold text-rose-600" data-testid="total-financial">{fmt(totals.total_financial_expense)}</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">{totals.creditor_payments_count} pagos | {isFiltered ? formatMonthLabel(selectedPeriod) : 'Total'}</p>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendUp size={16} className="text-[#0055FF]" />
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Ingreso Neto</p>
          </div>
          <p className={`text-xl font-bold ${totals.total_net_income >= 0 ? 'text-[#0055FF]' : 'text-[#E11D48]'}`} data-testid="total-net">{fmt(totals.total_net_income)}</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">Ingreso - Mantenimiento</p>
        </div>
        <div className="card p-5 border border-[#0055FF]/20 bg-blue-50/30">
          <div className="flex items-center gap-2 mb-1">
            <ChartBar size={16} className="text-[#0055FF]" />
            <p className="text-xs font-semibold text-[#0055FF] uppercase tracking-wider">Utilidad Neta</p>
          </div>
          <p className={`text-xl font-bold ${totals.total_net_after_financial >= 0 ? 'text-[#0055FF]' : 'text-[#E11D48]'}`} data-testid="total-utility">
            {fmt(totals.total_net_after_financial)}
          </p>
          <p className="text-xs text-[#94A3B8] mt-0.5">Neto - Gasto Financiero</p>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <Percent size={16} className="text-violet-500" />
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">ROI Promedio</p>
          </div>
          <p className="text-xl font-bold text-violet-600" data-testid="avg-roi">{fmtPct(totals.avg_roi)}</p>
          <p className="text-xs text-[#94A3B8] mt-0.5">{isFiltered ? formatMonthLabel(selectedPeriod) : 'Acumulado total'}</p>
        </div>
      </div>

      {/* Financial Expense Breakdown */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
            <Bank size={16} className="text-rose-500" />
            Desglose Gasto Financiero
          </h3>
          <span className="text-xs font-medium text-[#64748B] bg-[#F1F5F9] px-2 py-1 rounded">
            {isFiltered ? formatMonthLabel(selectedPeriod) : 'Acumulado Total'}
          </span>
        </div>
        {totals.financial_by_type && Object.keys(totals.financial_by_type).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {financialByTypeSorted.map(([type, amount]) => {
              const pct = totals.total_financial_expense > 0
                ? (amount / totals.total_financial_expense * 100).toFixed(1)
                : 0;
              return (
                <div key={type} className="bg-rose-50/50 rounded-lg p-4">
                  <p className="text-xs text-rose-600 mb-1 font-medium">{type}</p>
                  <p className="text-lg font-bold text-rose-700">{fmt(amount)}</p>
                  <div className="mt-2">
                    <div className="w-full h-1.5 bg-rose-100 rounded-full overflow-hidden">
                      <div className="h-full bg-rose-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-rose-400 mt-1">{pct}% del total</p>
                  </div>
                </div>
              );
            })}
            <div className="bg-rose-100/50 rounded-lg p-4 border border-rose-200">
              <p className="text-xs text-rose-700 mb-1 font-bold">TOTAL</p>
              <p className="text-lg font-bold text-rose-800">{fmt(totals.total_financial_expense)}</p>
              <p className="text-xs text-rose-500 mt-2">{totals.creditor_payments_count} pagos realizados</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#94A3B8]">No hay gastos financieros en este periodo</p>
        )}
      </div>

      {/* Top & Bottom Performer */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {topPerformer && (
          <div className="card p-5 border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Mejor Rendimiento</p>
              <span className="text-xs font-medium text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded">
                {isFiltered ? formatMonthLabel(selectedPeriod) : 'Total'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-bold text-[#0F172A]">{topPerformer.brand} {topPerformer.sub_brand}</p>
                <p className="text-xs text-[#64748B]">{topPerformer.plates} | {topPerformer.client_name}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-emerald-600">{fmt(topPerformer.net_income)}</p>
                <p className="text-xs text-emerald-500">
                  {isFiltered ? `${topPerformer.period_payments} pagos | ROI ${fmtPct(topPerformer.roi)}` : `ROI ${fmtPct(topPerformer.roi)}`}
                </p>
              </div>
            </div>
          </div>
        )}
        {bottomPerformer && bottomPerformer !== topPerformer && (
          <div className="card p-5 border-l-4 border-l-amber-500">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Menor Rendimiento</p>
              <span className="text-xs font-medium text-amber-500 bg-amber-50 px-2 py-0.5 rounded">
                {isFiltered ? formatMonthLabel(selectedPeriod) : 'Total'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-bold text-[#0F172A]">{bottomPerformer.brand} {bottomPerformer.sub_brand}</p>
                <p className="text-xs text-[#64748B]">{bottomPerformer.plates} | {bottomPerformer.client_name}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-amber-600">{fmt(bottomPerformer.net_income)}</p>
                <p className="text-xs text-amber-500">
                  {isFiltered ? `${bottomPerformer.period_payments} pagos | ROI ${fmtPct(bottomPerformer.roi)}` : `ROI ${fmtPct(bottomPerformer.roi)}`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Table */}
      <div className="card overflow-hidden">
        <table className="table-base" data-testid="profitability-table">
          <thead>
            <tr>
              <th>Vehiculo</th>
              <th>Cliente</th>
              <th>Estatus</th>
              <SortHeader field="weekly_rent" sortField={sortField} onSort={handleSort}>Renta</SortHeader>
              <SortHeader field="total_income" sortField={sortField} onSort={handleSort}>Ingreso</SortHeader>
              <SortHeader field="maintenance_cost" sortField={sortField} onSort={handleSort}>Mtto.</SortHeader>
              <SortHeader field="net_income" sortField={sortField} onSort={handleSort}>Neto</SortHeader>
              {isFiltered && <SortHeader field="period_payments" sortField={sortField} onSort={handleSort}>Pagos</SortHeader>}
              <SortHeader field="collection_rate" sortField={sortField} onSort={handleSort}>Cobranza</SortHeader>
              <SortHeader field="roi" sortField={sortField} onSort={handleSort}>ROI</SortHeader>
              <SortHeader field="roi_annualized" sortField={sortField} onSort={handleSort}>ROI Anual</SortHeader>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((v) => (
              <tr key={v.vehicle_id} className={v.contract_status !== 'activo' && v.contract_status !== 'vencido' ? 'opacity-50' : ''}>
                <td>
                  <div className="flex items-center gap-2">
                    <Car size={16} className="text-[#64748B]" />
                    <div>
                      <p className="text-sm font-semibold">{v.brand} {v.sub_brand}</p>
                      <p className="text-xs text-[#64748B]">{v.plates} | {v.year}</p>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <User size={14} className="text-[#94A3B8]" />
                    <span className="text-sm">{v.client_name}</span>
                  </div>
                </td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    v.contract_status === 'activo' ? 'bg-emerald-50 text-emerald-700' :
                    v.contract_status === 'vencido' ? 'bg-red-50 text-red-700' :
                    v.contract_status === 'cancelado' ? 'bg-gray-100 text-gray-600' :
                    'bg-amber-50 text-amber-700'
                  }`}>
                    {v.contract_status}
                  </span>
                </td>
                <td className="text-sm font-medium">{fmt(v.weekly_rent)}</td>
                <td className="text-sm font-medium text-emerald-600">{fmt(v.total_income)}</td>
                <td className="text-sm font-medium text-amber-600">{fmt(v.maintenance_cost)}</td>
                <td className={`text-sm font-bold ${v.net_income >= 0 ? 'text-emerald-600' : 'text-[#E11D48]'}`}>
                  {fmt(v.net_income)}
                </td>
                {isFiltered && (
                  <td className="text-sm font-medium text-[#0F172A]">{v.period_payments}</td>
                )}
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          v.collection_rate >= 90 ? 'bg-emerald-500' :
                          v.collection_rate >= 70 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(v.collection_rate, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium">{fmtPct(v.collection_rate)}</span>
                  </div>
                </td>
                <td>
                  <span className={`text-sm font-bold ${v.roi >= 0 ? 'text-[#0055FF]' : 'text-[#E11D48]'}`}>
                    {fmtPct(v.roi)}
                  </span>
                </td>
                <td>
                  <span
                    className={`text-sm font-bold ${(v.roi_annualized || 0) >= 0 ? 'text-indigo-700' : 'text-[#E11D48]'}`}
                    title={`${v.weeks_elapsed || 0} semanas transcurridas`}
                    data-testid={`roi-annual-${v.vehicle_id}`}
                  >
                    {fmtPct(v.roi_annualized)}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => setSelectedVehicle(v)}
                    className="p-1.5 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                    data-testid={`detail-${v.vehicle_id}`}
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedVehicle && (
        <div className="modal-overlay" data-testid="profitability-detail-modal">
          <div className="modal-content animate-fade-in max-w-2xl">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-bold">
                    {selectedVehicle.brand} {selectedVehicle.sub_brand} {selectedVehicle.model}
                  </h2>
                  <p className="text-sm text-[#64748B]">
                    {selectedVehicle.plates} | {selectedVehicle.year} | {selectedVehicle.color}
                    {isFiltered && <span className="ml-2 text-[#0055FF] font-medium">| {formatMonthLabel(selectedPeriod)}</span>}
                  </p>
                </div>
                <button onClick={() => setSelectedVehicle(null)} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F8FAFC] rounded-lg p-4">
                  <p className="text-xs text-[#64748B] mb-1">Cliente</p>
                  <p className="text-sm font-bold">{selectedVehicle.client_name}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-4">
                  <p className="text-xs text-[#64748B] mb-1">Estatus</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedVehicle.contract_status === 'activo' ? 'bg-emerald-50 text-emerald-700' :
                    'bg-amber-50 text-amber-700'
                  }`}>{selectedVehicle.contract_status}</span>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Inicio Contrato</p>
                  <p className="text-sm font-semibold">{selectedVehicle.start_date || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Fin Contrato</p>
                  <p className="text-sm font-semibold" data-testid="end-date">{selectedVehicle.end_date || '-'}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Renta Semanal</p>
                  <p className="text-sm font-semibold">{fmt(selectedVehicle.weekly_rent)}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-xs text-[#64748B]">Plazo</p>
                  <p className="text-sm font-semibold">{selectedVehicle.term_weeks} semanas</p>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#64748B]">Progreso de Pagos</span>
                  <span className="font-medium">{selectedVehicle.weeks_paid} / {selectedVehicle.weeks_elapsed} semanas transcurridas</span>
                </div>
                <div className="w-full h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#0055FF] rounded-full transition-all"
                    style={{ width: `${selectedVehicle.weeks_elapsed > 0 ? Math.min(selectedVehicle.weeks_paid / selectedVehicle.weeks_elapsed * 100, 100) : 0}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-xs text-emerald-600 mb-1">
                    {isFiltered ? 'Ingreso del Mes' : 'Ingreso Total'}
                  </p>
                  <p className="text-xl font-bold text-emerald-700">{fmt(selectedVehicle.total_income)}</p>
                  {isFiltered && (
                    <p className="text-xs text-emerald-500 mt-1">{selectedVehicle.period_payments} pagos en el mes</p>
                  )}
                  {!isFiltered && (
                    <p className="text-xs text-emerald-500 mt-1">Esperado: {fmt(selectedVehicle.total_expected)}</p>
                  )}
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-xs text-orange-600 mb-1">Penalizaciones Cobradas</p>
                  <p className="text-xl font-bold text-orange-700" data-testid="penalties-collected">{fmt(selectedVehicle.penalties_collected || 0)}</p>
                  <p className="text-xs text-orange-500 mt-1">Mora efectivamente pagada</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4">
                  <p className="text-xs text-teal-600 mb-1">Total de Accesorios</p>
                  <p className="text-xl font-bold text-teal-700" data-testid="accessories-collected">{fmt(selectedVehicle.accessories_collected || 0)}</p>
                  <p className="text-xs text-teal-500 mt-1">Cobros extra (llaves, siniestros, etc.)</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-xs text-amber-600 mb-1">Costo Mantenimiento</p>
                  <p className="text-xl font-bold text-amber-700">{fmt(selectedVehicle.maintenance_cost)}</p>
                </div>
                <div className="bg-rose-50 rounded-lg p-4">
                  <p className="text-xs text-rose-600 mb-1">Gasto Financiero (vehículo)</p>
                  <p className="text-xl font-bold text-rose-700">{fmt(selectedVehicle.financial_expense_own || 0)}</p>
                  {(!selectedVehicle.financial_expense_own || selectedVehicle.financial_expense_own === 0) && (
                    <p className="text-[10px] text-amber-600 mt-1">Sin acreedor asignado — edita el acreedor y vincúlalo a este vehículo</p>
                  )}
                  <p className="text-xs text-rose-400 mt-1">Pago del acreedor asignado × meses</p>
                </div>
                <div className="bg-violet-50 rounded-lg p-4">
                  <p className="text-xs text-violet-600 mb-1">Costo Adquisicion</p>
                  <p className="text-xl font-bold text-violet-700">{fmt(selectedVehicle.purchase_cost)}</p>
                </div>
                <div className="bg-blue-100/50 rounded-lg p-4 border border-blue-200 col-span-2">
                  <p className="text-xs text-blue-700 mb-1 font-semibold">Utilidad Neta</p>
                  <p className={`text-xl font-bold ${(selectedVehicle.net_utility ?? 0) >= 0 ? 'text-blue-700' : 'text-[#E11D48]'}`} data-testid="net-utility">
                    {fmt(selectedVehicle.net_utility ?? 0)}
                  </p>
                  <p className="text-xs text-blue-400 mt-1">(Ingresos + Penalizaciones + Accesorios) − Mantenimiento − Gasto Financiero</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-[#F8FAFC] rounded-lg">
                  <p className="text-2xl font-bold text-[#0055FF]" data-testid="roi-income">{fmtPct(selectedVehicle.roi)}</p>
                  <p className="text-xs text-[#64748B]">ROI sobre Ingresos</p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">(Ing + Penal + Acc) / Adquisición</p>
                </div>
                <div className="text-center p-3 bg-[#F8FAFC] rounded-lg">
                  <p className={`text-2xl font-bold ${(selectedVehicle.roi_utility ?? 0) >= 0 ? 'text-emerald-600' : 'text-[#E11D48]'}`} data-testid="roi-utility">{fmtPct(selectedVehicle.roi_utility)}</p>
                  <p className="text-xs text-[#64748B]">ROI Real sobre Utilidad</p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">Utilidad Neta / Adquisición</p>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <p className={`text-2xl font-bold ${(selectedVehicle.roi_annualized ?? 0) >= 0 ? 'text-indigo-700' : 'text-[#E11D48]'}`} data-testid="roi-annualized">{fmtPct(selectedVehicle.roi_annualized)}</p>
                  <p className="text-xs text-indigo-700 font-medium">ROI Anualizado</p>
                  <p className="text-[10px] text-indigo-500 mt-0.5">
                    {isFiltered
                      ? 'ROI Utilidad × 12 (mensual)'
                      : `ROI Utilidad × (52 / ${selectedVehicle.weeks_elapsed || 0} sem)`}
                  </p>
                </div>
                <div className="text-center p-3 bg-[#F8FAFC] rounded-lg">
                  <p className={`text-2xl font-bold ${(selectedVehicle.margin_pct ?? 0) >= 0 ? 'text-[#0F172A]' : 'text-[#E11D48]'}`} data-testid="margin-pct">{fmtPct(selectedVehicle.margin_pct)}</p>
                  <p className="text-xs text-[#64748B]">Margen</p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">Utilidad Neta / Ingresos</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg">
                <div>
                  <p className="text-xs text-[#64748B]">Saldo Pendiente del Contrato</p>
                  <p className="text-lg font-bold text-[#E11D48]">{fmt(selectedVehicle.current_balance)}</p>
                </div>
                {selectedVehicle.days_late > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-[#E11D48]">Dias de Atraso</p>
                    <p className="text-lg font-bold text-[#E11D48]">{selectedVehicle.days_late}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profitability;
